import { DynamicCarouselAndGrid, DynamicCarouselAndGridProps } from '../../sections/DynamicCarouselAndGrid/DynamicCarouselAndGrid'
import Image from 'next/image';
import { DynamicStructure } from '../../utils/interpolate';
import { ImageProps } from '../../utils/image/types';
import { getImageSrc } from '../../utils/image/getImageSrc';
import { getImageAlt } from '../../utils/image/getImageAlt';

// Helper para extrair src de backgroundImage em diferentes formatos
const getBackgroundImageSrc = (bgImage: any, device: 'desktop' | 'mobile'): string => {
  if (!bgImage) return '';

  // Formato: { desktop: { props: { src } } }
  if (bgImage[device]?.props?.src) {
    return bgImage[device].props.src;
  }

  // Formato: { desktop: { src } }
  if (bgImage[device]?.src) {
    return bgImage[device].src;
  }

  // Formato string direto
  if (typeof bgImage === 'string') {
    return bgImage;
  }

  return '';
};

// Transforma backgroundImage para ter funções render (necessário para o componente do renderer)
const transformBackgroundImage = (bgImage: any) => {
  if (!bgImage) return undefined;

  const desktopSrc = getBackgroundImageSrc(bgImage, 'desktop');
  const mobileSrc = getBackgroundImageSrc(bgImage, 'mobile') || desktopSrc;

  if (!desktopSrc && !mobileSrc) return undefined;

  return {
    desktop: {
      render: (props: ImageProps) => <Image {...props} width={1920} height={1080} sizes="100vw" loading="lazy" style={{ objectFit: 'cover', width: '100%', height: '100%' }} />,
      props: { src: desktopSrc, alt: '' },
    },
    mobile: {
      render: (props: ImageProps) => <Image {...props} width={768} height={1024} sizes="100vw" loading="lazy" style={{ objectFit: 'cover', width: '100%', height: '100%' }} />,
      props: { src: mobileSrc, alt: '' },
    },
  };
};

// Helper para encontrar backgroundImage em diferentes locais das props
const findBackgroundImage = (props: any) => {
  // Prioridade: props.backgroundImage > props.designConfig.backgroundImage
  if (props.backgroundImage && (getBackgroundImageSrc(props.backgroundImage, 'desktop') || getBackgroundImageSrc(props.backgroundImage, 'mobile'))) {
    return props.backgroundImage;
  }
  if (props.designConfig?.backgroundImage) {
    return props.designConfig.backgroundImage;
  }
  return undefined;
};

export default (props: DynamicCarouselAndGridProps<ImageProps> & { dynamicStructure?: DynamicStructure; typeMedia?: string; designConfig?: any }) => {
  const imageSrc = getImageSrc(props.cardConfig.image);
  const imageAlt = getImageAlt(props.cardConfig.image);

  // Pega backgroundImage do local correto
  const bgImage = findBackgroundImage(props);

  // Determina o backgroundType - pode vir de typeMedia, backgroundType ou designConfig
  const backgroundType = (props as any).typeMedia || props.backgroundType || props.designConfig?.backgroundType || 'color';

  const newProps = {
    ...props,
    buttons: props.buttons || [],
    cardConfig: {
      ...props.cardConfig,
      image: {
        render: (imageProps: ImageProps) => <Image {...imageProps} width={800} height={600} loading="lazy" />,
        props: {
          src: imageSrc,
          alt: imageAlt,
        },
      }
    },
    // Transforma backgroundImage para ter funções render
    backgroundImage: transformBackgroundImage(bgImage),
    // Passa backgroundType explicitamente para o componente
    backgroundType: backgroundType as 'image' | 'color' | 'video',
    // Video props (compatibilidade com dados salvos)
    typeVideo: (props as any).typeVideo || (props as any).designConfig?.typeVideo || 'url',
    urlVideo: (props as any).urlVideo || (props as any).designConfig?.urlVideo || '',
    embedVideo: (props as any).embedVideo || (props as any).designConfig?.embedVideo || '',
    loopVideo: (props as any).loopVideo ?? (props as any).designConfig?.loopVideo ?? true,
    audioVideo: (props as any).audioVideo ?? (props as any).designConfig?.audioVideo ?? false,
    shadowColor: (props as any).shadowColor || (props as any).designConfig?.backgroundOverlay || '#0000001a',
  }
  return <DynamicCarouselAndGrid {...newProps} dynamicStructure={props.dynamicStructure} />
}